import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EnterpriseAdminService } from '../../services/enterpriseAdminService';

// Same service keys as the customer/mechanic apps
const SERVICES = [
  { key: 'Towing', label: 'Towing' },
  { key: 'Jump Start', label: 'Jump Start' },
  { key: 'Flat Tyre', label: 'Tyre Change' },
  { key: 'Minor Repair', label: 'Minor Repair' },
  { key: 'Key Lockout', label: 'Key Lockout' },
  { key: 'Fuel Delivery', label: 'Fuel (delivery)' },
];

// Rate model per service: "up to X km → base ₹, then ₹Y/km".
// Day and Night each have their own base ₹ + ₹/km.
const TIMINGS = [
  { timing: 'day', group: 'Day' },
  { timing: 'night', group: 'Night' },
];

const SECTIONS = [
  { mode: 'instant', title: 'Instant' },
  { mode: 'scheduled', title: 'Future / scheduled' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const CITIES_BY_STATE = {
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Tirupati', 'Kurnool'],
  Assam: ['Guwahati', 'Silchar', 'Dibrugarh'],
  Bihar: ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur'],
  Chandigarh: ['Chandigarh'],
  Chhattisgarh: ['Raipur', 'Bhilai', 'Bilaspur'],
  Delhi: ['Delhi', 'New Delhi', 'Dwarka', 'Rohini'],
  Goa: ['Panaji', 'Margao', 'Vasco da Gama'],
  Gujarat: [
    'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar',
    'Gandhinagar', 'Junagadh', 'Anand', 'Morbi', 'Bharuch', 'Mehsana', 'Gandhidham',
  ],
  Haryana: ['Gurgaon', 'Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Hisar', 'Karnal', 'Rohtak'],
  'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Solan'],
  Jharkhand: ['Ranchi', 'Jamshedpur', 'Dhanbad'],
  Karnataka: ['Bangalore', 'Bengaluru', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum'],
  Kerala: ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Jabalpur', 'Gwalior', 'Ujjain'],
  Maharashtra: [
    'Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Thane', 'Navi Mumbai', 'Aurangabad',
    'Kolhapur', 'Solapur',
  ],
  Odisha: ['Bhubaneswar', 'Cuttack', 'Rourkela'],
  Puducherry: ['Puducherry'],
  Punjab: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Mohali'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem'],
  Telangana: ['Hyderabad', 'Warangal', 'Nizamabad'],
  'Uttar Pradesh': [
    'Lucknow', 'Kanpur', 'Noida', 'Ghaziabad', 'Agra', 'Varanasi', 'Meerut',
    'Prayagraj', 'Allahabad',
  ],
  Uttarakhand: ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
  'Jammu and Kashmir': ['Jammu', 'Srinagar'],
};

function combosFor(mode) {
  return TIMINGS.map((t) => ({ mode, timing: t.timing, group: t.group }));
}

function comboKey(service, c) {
  return `${service}|${c.mode}|${c.timing}`;
}

function scopeKey(def) {
  if (def.scope === 'state') return `state:${def.state}`;
  if (def.scope === 'city') return `city:${def.city}`;
  return 'all';
}

function scopeTitle(def) {
  if (def.scope === 'state') return `${def.state} (state)`;
  if (def.scope === 'city') return def.state ? `${def.city}, ${def.state} (city)` : `${def.city} (city)`;
  return 'All India (default)';
}

function emptyDraft() {
  return {
    baseKm: {},
    amount: {},
    perKm: {},
  };
}

function draftFromRows(rows) {
  const draft = emptyDraft();
  for (const r of rows) {
    const key = `${r.service_type}|${r.booking_mode}|${r.timing}`;
    if (Number(r.amount) > 0) draft.amount[key] = String(r.amount);
    if (Number(r.extra_per_km) > 0) draft.perKm[key] = String(r.extra_per_km);
    if (Number(r.base_km) > 0 && !draft.baseKm[r.service_type]) {
      draft.baseKm[r.service_type] = String(r.base_km);
    }
  }
  return draft;
}

function cleanNumberToken(raw) {
  const t = String(raw || '').replace(/[₹,\s]/g, '').trim();
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? t : null;
}

const cellInput =
  'w-[76px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500';
const kmInput =
  'w-[64px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500';

const EnterpriseRateCards = ({ enterpriseId }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [locations, setLocations] = useState({ states: [], cities: [] });
  const [settings, setSettings] = useState({
    rate_day_start: '06:00',
    rate_day_end: '22:00',
    allow_instant_accept_at_rate: false,
  });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');

  const [scopes, setScopes] = useState([{ scope: 'all' }]);
  const [drafts, setDrafts] = useState({ all: emptyDraft() });
  const [busyScope, setBusyScope] = useState(null);
  const [scopeMsg, setScopeMsg] = useState({});
  const [nightPlus, setNightPlus] = useState({});
  const [schedPlus, setSchedPlus] = useState({});

  const [newState, setNewState] = useState('');
  const [newCity, setNewCity] = useState('');
  const [typedCity, setTypedCity] = useState('');
  const [stateCities, setStateCities] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await EnterpriseAdminService.ratesGet(enterpriseId);
      setLocations(res.locations || { states: [], cities: [] });
      if (res.settings) setSettings(res.settings);

      const byScope = new Map();
      for (const row of res.cards || []) {
        const def =
          row.scope === 'state'
            ? { scope: 'state', state: row.state || '' }
            : row.scope === 'city'
              ? { scope: 'city', city: row.city || '', state: row.state || '' }
              : { scope: 'all' };
        const key = scopeKey(def);
        if (!byScope.has(key)) byScope.set(key, { def, rows: [] });
        byScope.get(key).rows.push(row);
      }

      const nextScopes = [{ scope: 'all' }];
      const nextDrafts = { all: draftFromRows(byScope.get('all')?.rows || []) };
      for (const [key, entry] of byScope) {
        if (key === 'all') continue;
        nextScopes.push(entry.def);
        nextDrafts[key] = draftFromRows(entry.rows);
      }
      setScopes(nextScopes);
      setDrafts(nextDrafts);
    } catch (err) {
      setLoadError(err.message || 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setNewCity('');
    setTypedCity('');
    if (!newState) {
      setStateCities([]);
      return;
    }
    let cancelled = false;
    EnterpriseAdminService.citiesForState(newState).then((list) => {
      if (!cancelled) setStateCities(list);
    });
    return () => {
      cancelled = true;
    };
  }, [newState]);

  const updateDraft = (key, updater) => {
    setDrafts((prev) => ({ ...prev, [key]: updater(prev[key] || emptyDraft()) }));
  };

  const setField = (key, field, mapKey, value) => {
    const v = value.replace(/[^\d.]/g, '');
    updateDraft(key, (draft) => ({
      ...draft,
      [field]: { ...draft[field], [mapKey]: v },
    }));
  };

  const flatColsFor = (mode) => {
    const cols = [{ type: 'baseKm' }];
    for (const c of combosFor(mode)) {
      cols.push({ type: 'amount', combo: c });
      cols.push({ type: 'perKm', combo: c });
    }
    return cols;
  };

  const handleGridPaste = (e, key, serviceIdx, colIdx, mode) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
    const grid = lines.map((l) => l.split(/[\t,;]| {2,}| /).map((t) => t.trim()));
    const isMulti = grid.length > 1 || (grid[0] && grid[0].length > 1);
    if (!isMulti) return;

    e.preventDefault();
    updateDraft(key, (draft) => {
      const cols = flatColsFor(mode);
      const next = {
        ...draft,
        baseKm: { ...draft.baseKm },
        amount: { ...draft.amount },
        perKm: { ...draft.perKm },
      };
      grid.forEach((tokens, lineOffset) => {
        const svc = SERVICES[serviceIdx + lineOffset];
        if (!svc) return;
        tokens.forEach((tok, tokOffset) => {
          const val = cleanNumberToken(tok);
          if (val == null) return;
          const col = cols[colIdx + tokOffset];
          if (!col) return;
          if (col.type === 'baseKm') next.baseKm[svc.key] = val;
          else if (col.type === 'amount') next.amount[comboKey(svc.key, col.combo)] = val;
          else if (col.type === 'perKm') next.perKm[comboKey(svc.key, col.combo)] = val;
        });
      });
      return next;
    });
  };

  // Shortcut: Night = Day + ₹X (per km copied as-is)
  const applyNightFromDay = (key) => {
    const plus = Number(nightPlus[key]) || 0;
    updateDraft(key, (draft) => {
      const amount = { ...draft.amount };
      const perKm = { ...draft.perKm };
      for (const s of SERVICES) {
        const day = Number(amount[`${s.key}|instant|day`]);
        if (day > 0) amount[`${s.key}|instant|night`] = String(Math.round(day + plus));
        const dayKm = perKm[`${s.key}|instant|day`];
        if (dayKm) perKm[`${s.key}|instant|night`] = dayKm;
      }
      return { ...draft, amount, perKm };
    });
  };

  // Copy Instant → Future. delta 0 = same, +100 = plus, -50 = minus. ₹/km copied as-is.
  const applySchedFromInstant = (key, deltaOverride) => {
    const raw = deltaOverride != null ? deltaOverride : schedPlus[key];
    const parsed = Number(String(raw ?? '').trim());
    const delta = Number.isFinite(parsed) ? parsed : 0;
    updateDraft(key, (draft) => {
      const amount = { ...draft.amount };
      const perKm = { ...draft.perKm };
      for (const s of SERVICES) {
        for (const t of ['day', 'night']) {
          const inst = Number(amount[`${s.key}|instant|${t}`]);
          if (inst > 0) amount[`${s.key}|scheduled|${t}`] = String(Math.max(0, Math.round(inst + delta)));
          const instKm = perKm[`${s.key}|instant|${t}`];
          if (instKm) perKm[`${s.key}|scheduled|${t}`] = instKm;
        }
      }
      return { ...draft, amount, perKm };
    });
  };

  // Copy one service row onto another in the same Instant/Future table.
  const pasteServiceRow = (key, mode, fromService, toService) => {
    if (!fromService || fromService === toService) return;
    updateDraft(key, (draft) => {
      const amount = { ...draft.amount };
      const perKm = { ...draft.perKm };
      const baseKm = { ...draft.baseKm };
      if (draft.baseKm[fromService]) baseKm[toService] = draft.baseKm[fromService];
      for (const t of ['day', 'night']) {
        const from = `${fromService}|${mode}|${t}`;
        const to = `${toService}|${mode}|${t}`;
        if (draft.amount[from]) amount[to] = draft.amount[from];
        else delete amount[to];
        if (draft.perKm[from]) perKm[to] = draft.perKm[from];
        else delete perKm[to];
      }
      return { ...draft, amount, perKm, baseKm };
    });
  };

  const saveSettings = async () => {
    setSettingsBusy(true);
    setSettingsMsg('');
    try {
      const res = await EnterpriseAdminService.rateSettings(enterpriseId, {
        dayStart: settings.rate_day_start,
        dayEnd: settings.rate_day_end,
        allowInstantAccept: !!settings.allow_instant_accept_at_rate,
      });
      setSettingsMsg(res?.success ? 'Saved' : res?.error || 'Failed to save');
    } catch (err) {
      setSettingsMsg(err.message || 'Failed to save');
    } finally {
      setSettingsBusy(false);
    }
  };

  const saveScope = async (def) => {
    const key = scopeKey(def);
    const draft = drafts[key] || emptyDraft();
    setBusyScope(key);
    setScopeMsg((m) => ({ ...m, [key]: '' }));
    try {
      const rows = [];

      for (const s of SERVICES) {
        const isFuel = s.key === 'Fuel Delivery';
        const baseKm = Number(draft.baseKm[s.key]) > 0 ? Number(draft.baseKm[s.key]) : null;
        for (const section of SECTIONS) {
          for (const c of combosFor(section.mode)) {
            const srcKey = comboKey(s.key, c);
            const amount = Number(draft.amount[srcKey]);
            const perKm = Number(draft.perKm[srcKey]) > 0 ? Number(draft.perKm[srcKey]) : null;
            const hasAmount = Number.isFinite(amount) && amount > 0;
            if (hasAmount) {
              rows.push({
                service_type: s.key,
                booking_mode: c.mode,
                timing: c.timing,
                amount,
                base_km: baseKm,
                extra_per_km: perKm,
              });
            } else if (isFuel && (baseKm != null || perKm != null)) {
              rows.push({
                service_type: s.key,
                booking_mode: c.mode,
                timing: c.timing,
                amount: 0,
                base_km: baseKm,
                extra_per_km: perKm,
              });
            }
          }
        }
      }

      const res = await EnterpriseAdminService.ratesUpsert(enterpriseId, {
        scope: def.scope,
        state: def.state || null,
        city: def.city || null,
        rows,
      });
      setScopeMsg((m) => ({
        ...m,
        [key]: res?.success ? `Saved (${res.saved ?? rows.length} rates)` : res?.error || 'Failed to save',
      }));
    } catch (err) {
      setScopeMsg((m) => ({ ...m, [key]: err.message || 'Failed to save' }));
    } finally {
      setBusyScope(null);
    }
  };

  const removeScope = async (def) => {
    const key = scopeKey(def);
    if (!window.confirm(`Remove ${scopeTitle(def)} rates?`)) return;
    setBusyScope(key);
    try {
      await EnterpriseAdminService.ratesDeleteScope(enterpriseId, {
        scope: def.scope,
        state: def.state || null,
        city: def.city || null,
      });
      setScopes((prev) => prev.filter((s) => scopeKey(s) !== key));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setBusyScope(null);
    }
  };

  const addScope = (def) => {
    const key = scopeKey(def);
    if (scopes.some((s) => scopeKey(s) === key)) return;
    setScopes((prev) => [...prev, def]);
    setDrafts((prev) => ({ ...prev, [key]: emptyDraft() }));
  };

  const copyFromAll = (key) => {
    const base = drafts.all;
    if (!base) return;
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        baseKm: { ...base.baseKm },
        amount: { ...base.amount },
        perKm: { ...base.perKm },
      },
    }));
  };

  const allStates = useMemo(
    () =>
      [...new Set([...(locations.states || []), ...INDIAN_STATES])]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [locations.states],
  );
  const stateRateExists = scopes.some((sc) => sc.scope === 'state' && sc.state === newState);
  const availableCities = useMemo(() => {
    if (!newState) return [];
    const used = new Set(
      scopes.filter((sc) => sc.scope === 'city').map((sc) => String(sc.city || '').toLowerCase()),
    );
    const seen = new Set();
    const out = [];
    const push = (name) => {
      const n = String(name || '').trim();
      if (!n) return;
      const k = n.toLowerCase();
      if (seen.has(k) || used.has(k)) return;
      seen.add(k);
      out.push(n);
    };
    for (const c of locations.cities || []) {
      if (String(c.state || '').toLowerCase() === newState.toLowerCase()) push(c.name);
    }
    for (const n of CITIES_BY_STATE[newState] || []) push(n);
    for (const n of stateCities) push(n);
    return out.sort((a, b) => a.localeCompare(b));
  }, [locations.cities, scopes, newState, stateCities]);
  const cityToAdd = (newCity || typedCity).trim();

  if (loading) {
    return <p className="py-8 text-center text-sm text-gray-500">Loading rate cards…</p>;
  }

  return (
    <div className="space-y-4">
      {loadError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{loadError}</p>
      )}

      {/* Day/night window + instant accept toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-bold text-gray-900">Day / night window</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Day rates apply inside this window (IST); night rates outside it.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Day starts</label>
            <input
              type="time"
              value={settings.rate_day_start}
              onChange={(e) => setSettings((s) => ({ ...s, rate_day_start: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Day ends</label>
            <input
              type="time"
              value={settings.rate_day_end}
              onChange={(e) => setSettings((s) => ({ ...s, rate_day_end: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={!!settings.allow_instant_accept_at_rate}
              onChange={(e) =>
                setSettings((s) => ({ ...s, allow_instant_accept_at_rate: e.target.checked }))
              }
            />
            Mechanics may accept instant jobs directly at the set rate
          </label>
          <button
            type="button"
            onClick={saveSettings}
            disabled={settingsBusy}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold disabled:opacity-60"
          >
            {settingsBusy ? 'Saving…' : 'Save settings'}
          </button>
          {settingsMsg && <span className="pb-2 text-xs font-semibold text-gray-500">{settingsMsg}</span>}
        </div>
      </div>

      {scopes.map((def) => {
        const key = scopeKey(def);
        const draft = drafts[key] || emptyDraft();
        const busy = busyScope === key;
        return (
          <div key={key} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-bold text-gray-900">{scopeTitle(def)}</h3>
              <div className="flex items-center gap-2">
                {def.scope !== 'all' && (
                  <>
                    <button
                      type="button"
                      onClick={() => copyFromAll(key)}
                      className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:border-gray-300"
                    >
                      Copy from All India
                    </button>
                    <button
                      type="button"
                      onClick={() => removeScope(def)}
                      disabled={busy}
                      className="px-2.5 py-1.5 border border-red-200 rounded-lg text-xs font-bold text-red-600 hover:border-red-300"
                    >
                      Remove
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => saveScope(def)}
                  disabled={busy}
                  className="px-4 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-bold disabled:opacity-60"
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
            {scopeMsg[key] && (
              <p className="mt-1 text-xs font-semibold text-gray-500">{scopeMsg[key]}</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Example: Towing — up to <b>10 km</b> base <b>₹1000</b>, then <b>₹30/km</b>. Extra km
              is go + return (mechanic goes to the job and comes back; 10 km one-way = 20 km). Night
              has its own base + ₹/km.
            </p>

            {SECTIONS.map((section) => {
              const combos = combosFor(section.mode);
              return (
                <div
                  key={section.mode}
                  className={section.mode === 'scheduled' ? 'mt-5 border-t border-gray-200 pt-4' : 'mt-4'}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-bold text-gray-800">{section.title}</h4>
                    {section.mode === 'instant' ? (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Night = Day +</span>
                        <span className="text-gray-400">₹</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={nightPlus[key] || ''}
                          onChange={(e) =>
                            setNightPlus((p) => ({ ...p, [key]: e.target.value.replace(/[^\d]/g, '') }))
                          }
                          placeholder="50"
                          className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => applyNightFromDay(key)}
                          className="px-2.5 py-1 bg-gray-800 text-white rounded-lg text-xs font-bold"
                        >
                          Apply
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Instant ±</span>
                        <span className="text-gray-400">₹</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={schedPlus[key] || ''}
                          onChange={(e) => {
                            const t = e.target.value.replace(/[^\d-]/g, '');
                            const neg = t.includes('-');
                            const digits = t.replace(/-/g, '');
                            setSchedPlus((p) => ({ ...p, [key]: (neg ? '-' : '') + digits }));
                          }}
                          placeholder="0"
                          className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm"
                          title="0 = same, 100 = plus, -50 = minus"
                        />
                        <button
                          type="button"
                          onClick={() => applySchedFromInstant(key)}
                          className="px-2.5 py-1 bg-gray-800 text-white rounded-lg text-xs font-bold"
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full min-w-[620px] text-left text-sm">
                      <thead>
                        <tr className="text-xs text-gray-400">
                          <th rowSpan={2} className="py-1 pr-2 font-medium align-bottom">Service</th>
                          <th rowSpan={2} className="px-1 py-1 font-medium align-bottom">
                            Up to
                            <span className="block text-[10px]">km</span>
                          </th>
                          {combos.map((c) => (
                            <th
                              key={`${c.mode}-${c.timing}`}
                              colSpan={2}
                              className="px-1 py-1 text-center font-bold text-gray-500 border-b border-gray-100"
                            >
                              {c.group}
                            </th>
                          ))}
                          <th rowSpan={2} className="px-2 py-1 font-medium align-bottom whitespace-nowrap">
                            Paste from
                          </th>
                        </tr>
                        <tr className="text-[11px] text-gray-400">
                          {combos.map((c) => (
                            <React.Fragment key={`${c.mode}-${c.timing}-sub`}>
                              <th className="px-1 py-1 font-medium">Base ₹</th>
                              <th className="px-1 py-1 font-medium">then ₹/km</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                          <tbody className="divide-y divide-gray-100">
                        {SERVICES.map((s, serviceIdx) => (
                          <tr key={`${section.mode}-${s.key}`}>
                            <td className="py-2 pr-2 font-semibold text-gray-700 whitespace-nowrap">{s.label}</td>
                            <td className="px-1 py-2">
                              <input
                                type="text"
                                inputMode="decimal"
                                name={`${key}-${section.mode}-${s.key}-baseKm`}
                                autoComplete="off"
                                value={draft.baseKm[s.key] || ''}
                                onChange={(e) => setField(key, 'baseKm', s.key, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onPaste={(e) => handleGridPaste(e, key, serviceIdx, 0, section.mode)}
                                placeholder="10"
                                className={kmInput}
                              />
                            </td>
                            {combos.flatMap((c, comboIdx) => {
                              const ck = comboKey(s.key, c);
                              return [
                                <td key={`${ck}-amount`} className="px-1 py-2">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    name={`${key}-${ck}-amount`}
                                    autoComplete="off"
                                    value={draft.amount[ck] || ''}
                                    onChange={(e) => setField(key, 'amount', ck, e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onPaste={(e) =>
                                      handleGridPaste(e, key, serviceIdx, 1 + comboIdx * 2, section.mode)
                                    }
                                    placeholder={c.timing === 'night' ? '1050' : '1000'}
                                    className={cellInput}
                                  />
                                </td>,
                                <td key={`${ck}-perKm`} className="px-1 py-2">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    name={`${key}-${ck}-perKm`}
                                    autoComplete="off"
                                    value={draft.perKm[ck] || ''}
                                    onChange={(e) => setField(key, 'perKm', ck, e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onPaste={(e) =>
                                      handleGridPaste(e, key, serviceIdx, 2 + comboIdx * 2, section.mode)
                                    }
                                    placeholder="30"
                                    className={kmInput}
                                  />
                                </td>,
                              ];
                            })}
                            <td className="px-2 py-2">
                              <select
                                defaultValue=""
                                onChange={(e) => {
                                  pasteServiceRow(key, section.mode, e.target.value, s.key);
                                  e.target.value = '';
                                }}
                                className="max-w-[140px] px-1.5 py-1 border border-gray-300 rounded-lg text-xs text-gray-600"
                                title="Copy another service's rates into this row"
                              >
                                <option value="">Paste from…</option>
                                {SERVICES.filter((src) => src.key !== s.key).map((src) => (
                                  <option key={src.key} value={src.key}>{src.label}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            <div className="mt-3 rounded-lg bg-gray-50 p-3">
              <p className="text-xs font-bold text-gray-600">Fuel Delivery</p>
              <p className="mt-1 text-xs text-gray-500">
                The Fuel row above is the <b>delivery charge</b> (up to km + extra ₹/km, round-trip).
                Petrol / Diesel ₹ per litre is <b>not</b> set here — it uses the live rate at the
                complaint location, same as the customer app.
              </p>
            </div>
          </div>
        );
      })}

      {/* Add scope */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-bold text-gray-900">Add location rate</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          District chhodo to rate poore state par lagega. Kisi city ki alag rate ho to district
          choose karo — city rate state se override karti hai.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">State</label>
            <select
              value={newState}
              onChange={(e) => setNewState(e.target.value)}
              className="min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select state…</option>
              {allStates.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              District / city
            </label>
            <select
              value={newCity}
              onChange={(e) => {
                setNewCity(e.target.value);
                setTypedCity('');
              }}
              disabled={!newState}
              className="min-w-[240px] px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            >
              <option value="">
                {newState ? `Entire ${newState} (all districts)` : 'Pick a state first'}
              </option>
              {availableCities.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">or type city</label>
            <input
              type="text"
              value={typedCity}
              onChange={(e) => {
                setTypedCity(e.target.value);
                setNewCity('');
              }}
              disabled={!newState}
              placeholder="optional"
              className="w-[140px] px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
          <button
            type="button"
            disabled={!newState || (cityToAdd ? false : stateRateExists)}
            onClick={() => {
              if (cityToAdd) {
                addScope({ scope: 'city', city: cityToAdd, state: newState });
                setNewCity('');
                setTypedCity('');
              } else {
                addScope({ scope: 'state', state: newState });
              }
            }}
            className="px-3 py-2 bg-primary-600 text-white rounded-lg text-xs font-bold disabled:opacity-60"
          >
            {cityToAdd
              ? `Add ${cityToAdd} rate`
              : stateRateExists
                ? 'State rate already added'
                : `Add entire ${newState || 'state'} rate`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseRateCards;
